class Form {
	// Matcher post/email-handler.php
	maxFileSize = 8 * 1024 * 1024;
	allowedFileTypes =
		/^(image\/(jpeg|png|gif|webp|heic|heif)|application\/pdf)$/;

	defaultValidations = {
		text: (field) => {
			const value = field.value.trim();
			console.log("Validating text field:", field.name, value, field.required);
			if (!field.required) {
				return false;
			}

			if (!value || value == "") return "Feltet er påkrævet";
			if (value.length <= 1) return "Indtast venligst mere end 1 karakter";

			return false;
		},

		tel: (field) => {
			if (!field.required) {
				return false;
			}

			let value = field.value.trim();
			if (!value) return "Telefonnummer påkrævet";
			value = value.replace(/\D/g, "");
			if (![8, 10, 12].includes(value.length)) return "Ugyldigt telefonnummer";

			return false;
		},

		email: (field) => {
			const value = field.value.trim();
			if (!field.required) {
				return false;
			}

			if (!value) return "Email påkrævet";

			const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
			if (!regex.test(value)) return "Ugyldig email";

			return false;
		},

		checkbox: (field) => {
			const checked = field.checked;
			if (!field.required) return false;

			if (!checked) return "Du skal acceptere vilkårene";

			return false;
		},

		file: (field) => {
			if (field.required && field.files.length === 0) return "Vælg en fil";

			for (const file of field.files) {
				if (file.size > this.maxFileSize)
					return "Filen er for stor (maks. 8 MB)";
				// Nogle browsere giver tom type for HEIC – lad serveren afgøre dem
				if (file.type && !this.allowedFileTypes.test(file.type))
					return "Kun billeder og PDF kan vedhæftes";
			}

			return false;
		},
	};

	constructor(selector, options = {}) {
		this.form = document.querySelector(selector);
		if (!this.form) {
			console.error("Couldn't find form with selector:", selector);
			return;
		}

		this.hasInteracted = false;
		this.sms = options.sms === "true";
		this.email = options.email === "true";
		this.fields = Array.from(this.form.elements);
		this.fields.forEach((field) => {
			["input", "blur", "focus"].forEach((eventType) => {
				field.addEventListener(eventType, () => {
					if (!this.hasInteracted) {
						this.hasInteracted = true;
						if (typeof Bugsnag !== "undefined") {
							Bugsnag.leaveBreadcrumb(
								`Interacted with form ID: ${this.form.id}`,
							);
						}
					}

					this.validateField(field, eventType, false);
				});
			});

			// Filfelter: valider straks ved valg (input-eventet rydder kun fejl)
			if (field.type === "file") {
				field.addEventListener("change", () => {
					const errorMessage = this.validations.file?.(field);
					if (errorMessage) this.errorHandler(field, errorMessage);
					else this.clearError(field);
				});
			}
		});

		this.validations = options.validations || this.defaultValidations;
		this.onSubmit = options.onSubmit || this.defaultSubmitHandler.bind(this);
		this.errorHandler =
			options.errorHandler || this.defaultErrorHandler.bind(this);

		this.form.addEventListener("submit", (e) => this.handleSubmit(e));
	}

	// Default error handler
	defaultErrorHandler(field, message) {
		const parentElement = field.parentElement;
		const insideInputContainer =
			parentElement.classList.contains("input-container");
		if (insideInputContainer) {
			parentElement.classList.add("input-error");
			parentElement.classList.remove("input-valid");
		} else {
			if (field.type === "checkbox" && field.name === "agree") {
				field.nextElementSibling.classList.add("input-error");
				field.nextElementSibling.classList.remove("input-valid");
			} else {
				field.classList.add("input-error");
				field.classList.remove("input-valid");
			}
		}

		const errorElement = insideInputContainer
			? field.type === "checkbox"
				? parentElement.parentElement.lastElementChild
				: parentElement.nextElementSibling
			: field.nextElementSibling;

		if (
			errorElement &&
			errorElement.classList.contains("input-error-message")
		) {
			errorElement.textContent = message;
		} else {
			const errorId = `${this.form.id}-${field.name}-error`;
			const newError = document.createElement("span");
			newError.className = "input-error-message";
			newError.textContent = message;
			newError.id = errorId;

			if (insideInputContainer) {
				//console.log("Inside input container");
				if (field.type === "checkbox") {
					//console.log("Checkbox parent element");
					parentElement.parentElement.appendChild(newError);
				} else {
					parentElement.insertAdjacentElement("afterend", newError);
				}
			} else {
				field.insertAdjacentElement("afterend", newError);
			}

			field.setAttribute("aria-invalid", true);
			field.setAttribute("aria-describedby", `${errorId}`);
		}
	}

	// Default submit handler
	async defaultSubmitHandler(token) {
		//console.log("Submitting form default handler");

		if (typeof Bugsnag !== "undefined") {
			Bugsnag.leaveBreadcrumb(`Submitting form ID: ${this.form.id}`);
		}

		this.clearNotification();
		const formDataObject = new FormData(this.form);
		formDataObject.append("token", token);
		const hasFiles = Array.from(formDataObject.values()).some(
			(value) => value instanceof File && value.size > 0,
		);
		const submitButton = this.form.querySelector('button[type="submit"]');
		try {
			const url = this.sms
				? `${settings.templateDir}/post/sms-handler.php`
				: `${settings.templateDir}/post/email-handler.php`;

			submitButton.disabled = true;
			const response = await fetch(url, {
				method: "POST",
				// Med filer: multipart (FormData). Uden: urlencoded som før.
				// URLSearchParams af en File giver strengen "[object File]".
				body: hasFiles
					? formDataObject
					: new URLSearchParams(
							Array.from(formDataObject.entries()).filter(
								([, value]) => !(value instanceof File),
							),
						),
			});

			if (!response.ok) {
				// handle
				submitButton.disabled = false;
				const message =
					response.status === 413
						? "<b>Filerne er for store.</b> Vedhæft mindre filer (maks. 8 MB pr. fil), eller send dem på e-mail."
						: response.status === 415
							? "<b>Filtypen understøttes ikke.</b> Du kan kun vedhæfte billeder og PDF."
							: "<b>Der skete en fejl!</b> Prøv venligst igen, ellers kontakt os direkte på telefon eller e-mail. Vi beklager ulejligheden.";
				this.displayNotification(message, "error");
				throw new Error("Submission failed");
			}

			const contentType = response.headers.get("content-type");
			let responseBody;
			if (contentType && contentType.includes("application/json")) {
				responseBody = await response.json();
			} else {
				responseBody = await response.text();
			}
			//console.log("Response body:", responseBody);

			this.displayNotification(
				"<b>Tak for din henvendelse!</b> Du vil blive kontaktet af os hurtigst muligt.",
				"success",
			);

			this.form
				.querySelectorAll(".input-valid")
				.forEach((field) => field.classList.remove("input-valid"));

			this.form.reset();
		} catch (error) {
			// handle
			submitButton.disabled = false;
			console.error("Submission error:", error);
		}
	}

	displayNotification(message, type = "success") {
		//console.log("Displaying notification");
		const notification = this.form.parentElement.querySelector("[role=alert]");
		if (!notification) {
			console.error("Couldn't find notification element");
			return;
		}

		notification.innerHTML = message;
		notification.setAttribute("type", type);
		notification.classList.remove("!hidden");
		const rect = notification.getBoundingClientRect();
		const isOutOfView =
			rect.bottom < window.innerHeight * 0.5 ||
			rect.top > window.innerHeight * 0.5;
		if (isOutOfView) {
			// console.log("Scrolling into view");
			// console.log(rect.bottom, rect.top, window.innerHeight);
			// console.log(rect.bottom < 0, rect.top > window.innerHeight);
			notification.scrollIntoView({ block: "end", behavior: "smooth" });
		}
	}

	clearNotification() {
		//console.log("Clearing notification");
		const notification = this.form.parentElement.querySelector("[role=alert]");
		if (!notification) {
			console.error("Couldn't find notification element");
			return;
		}

		notification.classList.add("!hidden");
	}

	// Handle submit
	handleSubmit(event) {
		//console.log("Handling submit event");
		event.preventDefault();

		if (this.validateForm()) {
			const onSubmit = this.onSubmit;
			grecaptcha
				.execute(settings.v3public, {
					action: `submit_${this.form.id.replace(/\-/g, "_")}`,
				})
				.then((token) => {
					onSubmit(token);
				});
		}
	}

	// Validate form fields
	validateForm() {
		let isValid = true;
		this.fields.forEach((field) => {
			const validation = this.validations[field.type];
			if (validation) {
				const errorMessage = validation(field);
				if (errorMessage) {
					isValid = false;
					this.errorHandler(field, errorMessage);
				} else {
					this.clearError(field);
				}
			}
		});
		return isValid;
	}

	validateField(field, eventType, strict = false) {
		// console.log(
		//   "Validating field:",
		//   field.name,
		//   "with event:",
		//   eventType,
		//   strict,
		// );

		if (eventType === "input") {
			this.clearError(field);
			return;
		}

		if (!field.hasAttribute("required")) {
			return;
		}

		if (eventType === "focus") {
			return;
		}

		if (eventType === "blur") {
			if (typeof Bugsnag !== "undefined") {
				Bugsnag.leaveBreadcrumb("Blurred input field", {
					formId: this.form.id,
					field: field.name,
					value: window.consent?.allowStatistics ? field.value : "REDACTED",
				});
			}
		}

		const validation = this.validations[field.type];
		if (validation && (strict || field.value)) {
			const errorMessage = validation(field);
			if (errorMessage) {
				if (typeof Bugsnag !== "undefined") {
					Bugsnag.leaveBreadcrumb("Input field has error in it", {
						formId: this.form.id,
						errorMessage,
						field: field.name,
						value: window.consent?.allowStatistics ? field.value : "REDACTED",
					});
				}
				this.errorHandler(field, errorMessage);
			} else {
				this.clearError(field);

				const parentElement = field.parentElement;
				const insideInputContainer =
					parentElement.classList.contains("input-container");
				if (insideInputContainer) {
					parentElement.classList.add("input-valid");
				} else {
					if (field.type === "checkbox" && field.name === "agree") {
						field.nextElementSibling.classList.add("input-valid");
					} else {
						field.classList.add("input-valid");
					}
				}
			}
		}
	}

	// Clear error messages
	clearError(field) {
		const parentElement = field.parentElement;
		const insideInputContainer =
			parentElement.classList.contains("input-container");
		if (insideInputContainer) {
			parentElement.classList.remove("input-error");
		} else {
			if (field.type === "checkbox" && field.name === "agree") {
				field.nextElementSibling.classList.remove("input-error");
			} else {
				field.classList.remove("input-error");
			}
		}

		field.removeAttribute("aria-invalid");
		field.removeAttribute("aria-describedby");

		const errorElement = insideInputContainer
			? field.type === "checkbox"
				? parentElement.parentElement.lastElementChild
				: parentElement.nextElementSibling
			: field.nextElementSibling;

		if (
			errorElement &&
			errorElement.classList.contains("input-error-message")
		) {
			errorElement.remove();
		}
	}
}

document.addEventListener("DOMContentLoaded", () => {
	if (window.pendingForms && window.pendingForms.length) {
		window.pendingForms.forEach((config) => {
			console.log("Registering form with config:", config);
			if (typeof Bugsnag !== "undefined") {
				Bugsnag.leaveBreadcrumb("Registering form", { config });
			}

			new Form(config.selector, config);
		});

		// Clear pendingForms after registration
		window.pendingForms = [];
	}
});
